import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux'
import { Row, Col, Card, CardHeader, Container, UncontrolledTooltip, Collapse } from "reactstrap";
import { useTranslation, Trans } from "react-i18next";
import { isArray } from "lodash";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFloppyDisk, faSave, faChevronRight, faChevronLeft, faCopy } from '@fortawesome/pro-light-svg-icons';
import { NotificationManager } from "react-notifications";
import reactStringReplace from 'react-string-replace';
import { copyToClipboard } from "../../utils/formValidations"

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const renderChildren = (children, section) => {
  return React.Children.map(children, (child) => {
  if (child) {
    if (child.type === React.Fragment || (child.type === Collapse && child.props.isOpen === true)) {
      return renderChildren(child.props.children, section)
    }
    if (child.props.component) {
      return <FormField key={child.props.name} section={section}>
        {child}
      </FormField>
    }
      return React.cloneElement(child, {
        section: section,
      });
    }
  });
}


const notifyMessage = (section, type, override, message ) => {
  let value = 'notify-' + type + '-' + message
  if (override) {
    value = override
  }
  return section + '.' + value
}

export function notifySuccess(props) {
  const { errorMessage, type, notifyTitle, notifySuccess, onSuccess } = props
  if (errorMessage) {
    NotificationManager.error(errorMessage, <Trans i18nKey={[notifyTitle || '', 'generic.notify-' + type + '-title']} ns="common" />, 3000);    
  } else {
    NotificationManager.success(<Trans i18nKey={[notifySuccess || '', 'generic.notify-' + type + '-success']} ns="common" />, <Trans i18nKey={[notifyTitle || '', 'generic.notify-' + type + '-title']} ns="common" />, 3000);
    if (typeof onSuccess === 'function') onSuccess()
  }
}

export function notifyFailure(props) {
  const { error, type, notifyTitle, onFailure } = props
  const errorMessage = error.response && error.response.body ? error.response.body : error.message
  if (errorMessage){
    NotificationManager.error(errorMessage, <Trans i18nKey={[notifyTitle || '', 'generic.notify-' + type + '-title']} ns="common" />, 3000);
  }
  if (typeof onFailure === 'function') onFailure()
}


export function Form(props) {
  const { tabList, currentTab, setCurrentTab, form, tabs, userData: additionalData, dataTransform, section, title, successMessage, dispatchFunc, type, onSuccess, onFailure } = props
  const { t } = useTranslation('common');
  const dispatch = useDispatch()

  let passType = type ? type : 'create'

  const notifyTitle = notifyMessage(section, type: passType, title, 'title')
  const notifySuccessMessage = notifyMessage(section, type: passType, successMessage, 'success')

  const passSave = async(userData) => {
    let newData = {
      ...userData,
      ...additionalData
    }
    if (dataTransform) {
      newData = dataTransform(newData)
      if (newData === null) {
        return false
      }
    }
    try {
      const { response: { error_message: errorMessage } } = await dispatch(dispatchFunc(newData))
      notifySuccess({
        errorMessage,
        type: passType,
        notifyTitle,
        notifySuccess: notifySuccessMessage,
        onSuccess
      })
    } catch(error) {
      notifyFailure({
        error,
        type: passType,
        notifyTitle,
        onFailure
      })
    }

}
const setForm = React.cloneElement(form, {
  save: passSave,
  section,
  fromUpdate: (type && type === 'update') || false
})

  return (
    <Row>
      <Col sm={{ size: 10, order: 1, offset: 1 }}>
        <TabList {...props} tabList={tabList} currentTab={currentTab} setCurrentTab={setCurrentTab} />
        <div className={currentTab === 'form' ? 'tw-block' : 'tw-hidden'}>
          {setForm}
        </div>
        {tabs}
      </Col>
    </Row>
  )
}

export function Groups(props) {
  const { section, children, onSubmit, noPadding, className, encType = null } = props
  const noPaddingClass = noPadding ? '' : ' tw-mt-12 tw-gap-8 tw-pb-32'
  
  return (
    <form onSubmit={onSubmit} encType={encType}>
      <div className={classNames("tw-flex tw-flex-col", noPaddingClass, className)}>
        {children && renderChildren(children, section)}
      </div>
    </form>
  )
}

export function Group(props) {
  const { section, title, description, tooltip, children } = props
  const { t } = useTranslation('common');
  return (
    <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 lg:tw-gap-16 tw-border-0 tw-border-solid tw-pb-8 tw-border-b tw-border-black/10">
      <div className="tw-flex tw-flex-col tw-w-full tw-max-w-xs">
        {tooltip && (
          <UncontrolledTooltip placement="right" target={"group-" + title.replace(/[^a-z0-9]/gi, '')}>
            {t(section + '.' + tooltip)}
          </UncontrolledTooltip>
        )}
        <div className="tw-font-bold tw-mb-4"><span id={"group-" + title.replace(/[^a-z0-9]/gi, '')}>{t(section + '.' + title)}</span></div>
        <div className="text-muted-more tw-leading-6">{t(section + '.' + description)}</div>
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col">
        {children && renderChildren(children, section)}
      </div>
    </div>
  )
}

export function CopyToClipboard(props) {
  return (
    <button type="button" className={classNames("tw-ml-2 tw-bg-transparent tw-cursor-pointer tw-group/clip", props.className)} onClick={() => copyToClipboard(props.value)}>
      <FontAwesomeIcon className="tw-transition-all group-hover/clip:tw-scale-110" icon={faCopy} />
    </button>
  )
}

export function Button(props) {
  const {section, icon, name, color, type, onClick, buttonRef, className, full, large} = props
  const { t } = useTranslation('common');
  const displayIcon = icon === "save" ? <FontAwesomeIcon icon={faFloppyDisk} /> : icon
  const displayColor = color ? color : 'tw-bg-blue-500'
  return (
    <button ref={buttonRef} onClick={onClick} type={type ? type : 'button'} className={classNames("tw-rounded hover:tw-bg-slate-600 tw-text-sm tw-text-white tw-flex tw-items-center tw-transition", displayColor, className, (large ? 'tw-h-12' : 'tw-h-10'), full ? 'tw-w-full' : 'tw-w-auto')}>
      <span className={"tw-flex tw-justify-center tw-items-center tw-bg-black/10" + (large ? ' tw-h-12 tw-w-16 ' : ' tw-h-10 tw-w-12 ')}>{displayIcon}</span>
      <span className={"tw-px-4 tw-flex-1" + (large ? ' tw-pr-16' : ' tw-pr-10')}>{t(section + '.' + name)}</span>
    </button>
  )
}

export function FormField(props) {
  const { section, tooltip, label, rawLabel, children, className, resetSection, additional = '' } = props
  const prefix = (!resetSection) ? section + '.' : ''
  const tooltipRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const { t } = useTranslation('common');

  React.useEffect(() => {
    if (tooltipRef.current) {
      setReady(true);
    }
  }, [tooltipRef]);

  let required = false
  if (children.props && children.props.required) {
    required = true
  }
  let displayLabel = ''
  let kebabLabel = ''
  if (children.props && children.props.name) {
    displayLabel = prefix + children.props.name
    kebabLabel = prefix + children.props.name.replace(/_/g, '-')
  }
  if (label) {
    displayLabel = prefix + label
    kebabLabel = prefix + label.replace(/_/g, '-')
  }
  const tooltipId = (children.props.id) ? 'tooltip-' + children.props.id : null
  return (
    <div className={classNames("kasm-form-field tw-w-full tw-max-w-lg tw-mb-6", className)}>
      {children.props && tooltip && ready && (
        <UncontrolledTooltip key="tooltipId"  placement="right" target={tooltipId}>
          {t(prefix + tooltip)}
        </UncontrolledTooltip>
      )}
      <div className="tw-flex tw-flex-col">
        <label htmlFor={children.props && children.props.id} className={"tw-font-bold" + (required ? ' requiredasterisk' : '')}>
          <span ref={tooltipRef} id={tooltipId}>
            {rawLabel ? rawLabel :
            reactStringReplace(t([displayLabel, kebabLabel]), /\((.*?)\)/, (match, i) => (
            <span className="text-muted-more tw-text-xs">({match})</span>
            ))}
         
            </span>
        </label>
        {additional && (
          <div className="tw-mb-2 -tw-mt-3">
            {additional}
          </div>

        )}
      </div>
      {children}
    </div>
  )
}

export function FormFooter(props) {
  const { section, children, cancel, cancelButton = false, saveButton = false, className, inline = false } = props
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true)
    }, 250)
  }, []);

  const move = loaded ? 'tw-transition-all tw-translate-y-0' : 'tw-translate-y-32'
  const inlineClass = inline ? '' : 'form-footer tw-fixed tw-py-6 tw-px-16 tw-bottom-0 tw-right-0 lg:tw-right-6 tw-left-0 lg:tw-left-6 tw-bg-[image:var(--bg-trans)] tw-backdrop-blur-sm'

  return (
    <div className={classNames("tw-flex  tw-rounded-t-lg tw-justify-center tw-gap-6", className, move, inlineClass)}>
      {!cancelButton ? <button className="cancel-button tw-bg-transparent" type="button" onClick={() => cancel()}> {t('buttons.Cancel')}</button> : cancelButton}
      {!saveButton ? <Button id="save-form" icon="save" type="submit" section="buttons" name="Save" /> : saveButton}
    </div>
  )
}

export function submitFailure(errors) {
    const firstError = Object.keys(errors)[0];
    const el = document.querySelector(`[id="${firstError}"]`);
    const position = el.getBoundingClientRect().top + document.documentElement.scrollTop;
  
    const offset = 100;
  
    window.scrollTo({ top: position - offset, behavior: 'smooth' });
}

export function ViewField(props) {
  const { component } = props
  const details = { input: {...props}, disabled: true, copy: true, meta: { touched: false, error: false, warning: false } }
  if (component === 'blank') {
    return <div className="input"><div className="textarea">{props.value}</div></div>
  }
  return component(details)
}

export function TabList(props) {
  const { tabList, currentTab, setCurrentTab } = props;
  const [tabWidth, setTabWidth] = useState(0);
  const [initial, setInitial] = useState(true);
  const [availableWidth, setAvailableWidth] = useState(0);
  const { t } = useTranslation('common');
  const tabsRef = useRef(null)
  const addArrows = tabWidth > availableWidth

  useEffect(() => {
    setTimeout(() => {
      setTabWidth(tabsRef.current.scrollWidth)
      setAvailableWidth(tabsRef.current.offsetWidth)
    }, 500)
  }, [])

  useEffect(() => {
    setTabWidth(tabsRef.current.scrollWidth)
    setAvailableWidth(tabsRef.current.offsetWidth)
  }, [addArrows])

  const updateTab = (location) => {
      const { search } = location
      const tab = new URLSearchParams(search).get('tab') || tabList[0].key
      if (tab !== currentTab) {
        setCurrentTab(tab)
      }
  }

  useEffect(() => {
    if (props.location) {
      // if browser back/forward buttons are used
      props.history.listen((loc, action) => {
        if (action === "POP") {
          updateTab(loc)
        }
      });
      // Initial browser load
      updateTab(props.location)
    }
  }, []);

  // Update history when tab is clicked
  useEffect(() => {
    if (props.history && !initial) {
      const params = new URLSearchParams(props.location.search)
      if (currentTab === tabList[0].key) {
        params.delete('tab') // on first tab, don't want it to set a search param, but do want it to update the history
      } else {
        params.set('tab', currentTab)
      }
      props.history.push({ pathname: props.location.pathname, search: params.toString() });
    }
    if (initial) {
      setInitial(false)
    }
  }, [currentTab]);


  const scrollForward = () => {
    const amount = tabsRef.current.scrollLeft + (availableWidth - 80)
    tabsRef.current.scrollTo({
      top: 0,
      left: amount,
      behavior: 'smooth'
    })
  }
  const scrollBackward = () => {
    const amount = tabsRef.current.scrollLeft - (availableWidth - 80)
    tabsRef.current.scrollTo({
      top: 0,
      left: amount,
      behavior: 'smooth'
    })
  }

  return (
    <React.Fragment>
      <div className="tw-flex tw-w-full tw-mb-6">
      {addArrows && (
            <div onClick={() => scrollBackward()} className="tw-w-8 tw-rounded tw-flex tw-items-center tw-justify-center -tw-ml-6 tw-text-xl"><FontAwesomeIcon icon={faChevronLeft} /></div>
          )}

          <nav ref={tabsRef} className={"tw-border-b tw-border-0 tw-border-solid tw-border-[color:var(--border-color)] tab-nav tw-block lg:tw-w-full tw-flex-1 tw-space-x-8 tw-whitespace-nowrap tw-overflow-x-auto"} aria-label={t('tables.tabs')}>
            {tabList && tabList.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setCurrentTab(tab.key)}
                className={classNames(
                  tab.key === currentTab
                    ? 'tw-border-blue-500 tw-font-bold'
                    : 'tw-border-transparent text-muted-extra hover:tw-border-blue-500/60 hover:tw-text-gray-700',
                  'tw-group tw-inline-flex tw-items-center tw-border-b-2 tw-border-0 tw-border-solid tw-py-4 tw-px-1 tw-font-semibold tw-bg-transparent'
                )}
                aria-current={tab.current ? 'page' : undefined}
              >
                <span>{t(tab.name)}</span>
              </button>
            ))}
          </nav>
        {addArrows && (
            <div onClick={() => scrollForward()} className="tw-w-8 tw-rounded tw-flex tw-items-center tw-justify-center -tw-mr-6 tw-text-xl"><FontAwesomeIcon icon={faChevronRight} /></div>
          )}

      </div>
    </React.Fragment>
  )
}
